import React, { JSX, useContext, useEffect } from "react";
import Head from "next/head";
import { colors } from "constants/colors";
import { LogContext } from "context/log";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { generatePageId } from "@pepdirect/v3/helpers/analyticsLogger";

import st from "./news.module.scss";

// Replica of drinkpoppi.com/blogs/news (QA parity report flagged the blog as
// missing). Live's blog is PUBLISHED BUT EMPTY: zero articles in the server
// HTML, the atom feed, and the rendered DOM — only the theme scaffold renders
// (pink featured band, "refreshing reads" heading, empty grid, yellow closing
// wave). This mirrors that scaffold, minus prod's broken empty-hero Liquid
// error. Post cards get added here when editorial content exists.

interface BlogPost {
  slug: string;
  title: string;
  date: string;
  image: string;
}

// no posts exist on prod (verified against server HTML + atom feed +
// rendered DOM on 2026-08-19)
const POSTS: BlogPost[] = [];

function WaveLarge({ className, fill }: { className?: string; fill: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 1440 300" fill="none" aria-hidden="true" preserveAspectRatio="none">
      <path
        d="M290.085 131.479C152.295 131.479 91.8603 36.6858 -2.40927e-05 36.6858L-3.05176e-05 0.000244141L1440 0V225.821C1354.32 264.938 1251.9 300 1133.75 300C901.682 300 795.317 94.615 587.423 94.615C437.545 94.615 427.876 131.479 290.085 131.479Z"
        fill={fill}
      />
    </svg>
  );
}

export default function BlogNews(): JSX.Element {
  const { useLogPageView } = useContext(LogContext);

  useEffect(() => {
    const body = document.body;
    body.style.setProperty("--color-primary", colors.magenta);
    body.style.setProperty("--logo-color", "#ffffff");
    body.style.setProperty("--page-top", "0px");
    return () => {
      body.style.removeProperty("--color-primary");
      body.style.removeProperty("--logo-color");
      body.style.removeProperty("--page-top");
    };
  }, []);

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.support,
    pageCategory: undefined,
    pageTitle: `Blog – ${META_TITLE}`,
    id: generatePageId("blog-news-page"),
  });

  return (
    <div className={st.page}>
      <Head>
        <title>{`Blog – ${META_TITLE}`}</title>
        <link rel="canonical" href={`${CANONICAL_URL_BASE}/blogs/news`} />
        <meta
          name="description"
          content="refreshing reads — news and stories from poppi, the prebiotic soda."
          key="description"
        />
      </Head>

      {/* ---------------- featured band (pink, wave into white) ---------------- */}
      <section className={st.featured}>
        <WaveLarge className={st.featuredWave} fill={colors.magenta} />
      </section>

      {/* ---------------- article grid ---------------- */}
      <section className={st.reads}>
        <h2>refreshing reads</h2>
        {POSTS.length === 0 ? (
          <p className={st.empty}>new stories are popping soon — check back!</p>
        ) : (
          <div className={st.grid}>
            {POSTS.map((post) => (
              <a key={post.slug} href={`/blogs/news/${post.slug}`} className={st.card}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={post.image} alt="" />
                <h3>{post.title}</h3>
                <span>{post.date}</span>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* ---------------- closing yellow wave ---------------- */}
      <section className={st.closing}>
        <WaveLarge className={st.closingWave} fill={colors.yellow} />
      </section>
    </div>
  );
}
